import { Button } from "@/components/ui/button";
import { useBookingFlow } from "../../context/use-booking-flow";
import { getPoTypeLabel } from "@/features/license-fee/list/utils/private-office-contract-mappings";
import { CycleDetails } from "../cycle-details";
import { PackageDetails } from "../package-details";
import { AdditionalPaymentsTable } from "../additional-payments-table";
import { UpsellCyclesTable } from "../updated-cycles-table";
import { BookingFlowHeader } from "../booking-flow-header";
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
  CardHeader,
} from "@/components/ui/card";
import { usePoContractProrataMemoryPreview } from "@/hooks/use-po-contract-prorata-memory-preview/use-po-contract-prorata-memory-preview";
import { PrivateOfficeProRataMemory } from "@/components/private-office-prorata-memory";
import { safeFormatInTimeZone } from "@/utils/format/safe-format-in-time-zone";

export const BookingSummary = () => {
  const {
    bookingData,
    upsellData,
    previousStep,
    nextStep,
    flowType,
    selectedCycle,
    selectedPackage,
    selectedPackageGroups,
    poContractCreateData,
  } = useBookingFlow();

  const poTypeLabel = getPoTypeLabel(poContractCreateData?.poType);

  // Só o tipo "po" tem pró-rata nativo; "bts"/"tax_address"/etc. não passam
  // por esse cálculo. Sem `contractId` ainda (contrato só nasce no POST
  // desta tela), então é sempre a simulação por packageId/companyId, nunca
  // a memória de um contrato já existente.
  // Mesma regra do backend (gatilho de entrada): só friccao_zero com
  // pró-rata aplicável gera memória. Com "anterior" a prévia mentiria sobre
  // um consumo proporcional que não vai acontecer.
  const isPoDraftReady =
    poContractCreateData?.contractType === "po" &&
    poContractCreateData.commercialPolicy === "friccao_zero" &&
    Boolean(poContractCreateData.prorataApplicable) &&
    !!poContractCreateData.packageId &&
    !!poContractCreateData.companyId &&
    !!poContractCreateData.startDate;

  const { proRataMemory, isLoadingProRataMemory, proRataMemoryError } =
    usePoContractProrataMemoryPreview({
      packageId: isPoDraftReady
        ? Number(poContractCreateData!.packageId)
        : undefined,
      companyId: isPoDraftReady
        ? Number(poContractCreateData!.companyId)
        : undefined,
      // date-only (yyyy-MM-dd) em UTC, como o resto das datas de contrato
      // nesta tela — mesma convenção do fluxo de edição.
      startDate: isPoDraftReady
        ? safeFormatInTimeZone(
            poContractCreateData!.startDate,
            "UTC",
            "yyyy-MM-dd",
            "",
          )
        : undefined,
      // Rascunho com prazo determinado já define endDate: sem isso a prévia
      // cobraria até o fim do ciclo inteiro para um contrato curto, mentindo
      // sobre o valor que será de fato cobrado.
      endDate: safeFormatInTimeZone(
        poContractCreateData?.endDate,
        "UTC",
        "yyyy-MM-dd",
      ),
      monthlyCredits: poContractCreateData?.monthlyCredits,
      takePercentage: poContractCreateData?.takeRatePercentage,
    });

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <BookingFlowHeader
        onClick={() => previousStep()}
        title="Resumo"
        subtitle="Revise as informações desta reserva antes de confirmar sua criação."
      />
      <div className="space-y-6 mt-6">
        <div className="border rounded-lg p-4">
          <div className="pb-4 flex-col justify-center items-start gap-1.5 inline-flex">
            <div className="text-2xl font-semibold font-['Inter'] leading-loose">
              Reserva
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 pb-4">
            <div>
              <p className="font-medium">{`${bookingData?.company.name} | ID = ${bookingData?.company.id}`}</p>
              <p className="text-sm text-gray-500">Companhia</p>
            </div>
            <div>
              <p className="font-medium">{selectedCycle?.id}</p>
              <p className="text-sm text-gray-500">ID do ciclo do pacote</p>
            </div>
            {bookingData?.private_room.name ? (
              <div>
                <p className="font-medium">
                  {bookingData?.private_room.name.split("|")[0]}
                </p>
                <p className="text-sm text-gray-500">Escritório privativo</p>
              </div>
            ) : (
              <div>
                <p className="font-medium">Sem escritório específico</p>
                <p className="text-sm text-gray-500">Escritório privativo</p>
              </div>
            )}
            {bookingData?.private_room.searchId && (
              <div>
                <p className="font-medium">
                  {bookingData?.private_room.searchId}
                </p>
                <p className="text-sm text-gray-500">
                  ID do escritório privativo
                </p>
              </div>
            )}
            {poContractCreateData?.contractType === "po" && poTypeLabel && (
              <div>
                <p className="font-medium">{poTypeLabel}</p>
                <p className="text-sm text-gray-500">Tipo de escritório</p>
              </div>
            )}
          </div>
        </div>
        <Card>
          <CardHeader>
            <CardTitle className="self-stretch text-foreground text-2xl font-medium">
              Pacote e Ciclo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Card className="flex bg-slate-50">
              {selectedPackage && selectedPackageGroups && (
                <div className="flex-1">
                  <PackageDetails
                    selectedPackage={selectedPackage}
                    packageGroups={selectedPackageGroups}
                    className="border-r-2"
                  />
                </div>
              )}
              {selectedCycle && (
                <div className="flex-1">
                  <CycleDetails
                    activeCycle={selectedCycle}
                    additionalCredits={Number(bookingData?.credits) || 0}
                    upsellCredits={upsellData?.upsellOption || 0}
                  />
                </div>
              )}
            </Card>
          </CardContent>
        </Card>
        {flowType === "upsell" && upsellData && (
          <>
            {upsellData && (
              <>
                <Card className="w-full px-2">
                  <CardHeader>
                    <CardTitle className="self-stretch text-foreground text-2xl font-medium">
                      Pagamentos adicionais
                    </CardTitle>
                    <CardDescription className="self-stretch text-muted-foreground text-sm font-normal leading-tight">
                      {upsellData?.simulatedUpsellData.newPayments &&
                        "Os seguintes pagamentos serão cobrados."}
                    </CardDescription>
                  </CardHeader>
                  {upsellData?.simulatedUpsellData.newPayments && (
                    <CardContent>
                      <AdditionalPaymentsTable
                        data={upsellData?.simulatedUpsellData.newPayments}
                      />
                    </CardContent>
                  )}
                </Card>

                <Card className="w-full px-2">
                  <CardHeader>
                    <CardTitle className="self-stretch text-foreground text-2xl font-medium">
                      Ciclos afetados
                    </CardTitle>
                    <CardDescription className="self-stretch text-muted-foreground text-sm font-normal leading-tight">
                      {upsellData?.simulatedUpsellData?.updatedCycles
                        ? "Revise abaixo como os ciclos ao longo da reserva serão impactados por ela."
                        : "Selecione um valor de créditos adicionais para mais informações."}
                    </CardDescription>
                  </CardHeader>
                  {upsellData?.simulatedUpsellData?.updatedCycles && (
                    <CardContent>
                      <UpsellCyclesTable
                        data={upsellData?.simulatedUpsellData.updatedCycles}
                      />
                    </CardContent>
                  )}
                </Card>
              </>
            )}
          </>
        )}
        <PrivateOfficeProRataMemory
          data={proRataMemory}
          isLoading={isLoadingProRataMemory}
          error={proRataMemoryError}
          pendingActionLabel="criar a reserva"
        />
      </div>

      <div className="mt-8 flex justify-end gap-4">
        <Button variant="outline" onClick={previousStep} className="px-4 py-2">
          Voltar
        </Button>
        <Button onClick={nextStep} className="px-4 py-2">
          Criar reserva
        </Button>
      </div>
    </div>
  );
};
